import _toConsumableArray from "/workdir/node_modules/next/dist/compiled/@babel/runtime/helpers/esm/toConsumableArray.js";
import { createRef, useCallback, useEffect, useId, useRef, useState } from 'react';
import { useKey } from 'react-use';
import useScrollTo from '@segment/utils/useScrollTo';
/**
 * A hook for managing tab state and providing tab props.
 * @param {number} initialTab - The index of the initially active tab.
 * @param {number} numberOfTabs - Total number of tabs to cycle through.
 * @param {string} id - The id of the tab instance.
 *
 * @returns {object} An object containing several utility functions and state values for tab management.
 * @returns {number} activeTabKey - The index of the currently active tab.
 * @returns {function} setActiveTabKey - A function to set the active tab.
 * @returns {function} getControlProps - A function to get props for a tab control.
 * @returns {function} getPanelProps - A function to get props for a tab panel.
 * @returns {function} isActive - A function to check if a tab is active.
 */

var useTabs = function useTabs() {
  var initialTab = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : 0;
  var numberOfTabs = arguments.length > 1 ? arguments[1] : undefined;
  var id = arguments.length > 2 ? arguments[2] : undefined;

  var _useState = useState(initialTab),
      activeTabKey = _useState[0],
      setActiveTabKey = _useState[1];

  var trackRef = useRef(null);
  var scrollTo = useScrollTo(trackRef);
  var tabRefs = Array.from({
    length: numberOfTabs
  }, function () {
    return /*#__PURE__*/createRef();
  });
  var uniqueTabIds = useRef(_toConsumableArray(Array(numberOfTabs)).map(function () {
    return "".concat(id, "-").concat(useId());
  }));

  var isEventFromTabRef = function isEventFromTabRef(e) {
    return tabRefs.some(function (ref) {
      return ref.current === e.target;
    });
  };

  var changeTab = useCallback(function (newTab) {
    if (newTab >= 0 && newTab < numberOfTabs) {
      setActiveTabKey(newTab);
    }
  }, []);
  var arrowRightHandler = useCallback(function (e) {
    if (!isEventFromTabRef(e)) return;
    var nextTab = (activeTabKey + 1) % numberOfTabs;
    changeTab(nextTab);
    scrollTo(tabRefs[nextTab]);
  }, [activeTabKey, numberOfTabs]);
  var arrowLeftHandler = useCallback(function (e) {
    if (!isEventFromTabRef(e)) return;
    var nextTab = (activeTabKey - 1 + numberOfTabs) % numberOfTabs;
    changeTab(nextTab);
    scrollTo(tabRefs[nextTab]);
  }, [activeTabKey, numberOfTabs]);
  useEffect(function () {
    var isAnyTabFocused = Object.values(tabRefs).some(function (ref) {
      return document.activeElement === ref.current;
    });

    if (tabRefs[activeTabKey].current && isAnyTabFocused) {
      var _tabRefs$activeTabKey;

      (_tabRefs$activeTabKey = tabRefs[activeTabKey].current) === null || _tabRefs$activeTabKey === void 0 ? void 0 : _tabRefs$activeTabKey.focus();
    }
  }, [activeTabKey, tabRefs]);
  var isActive = useCallback(function (value) {
    return value === activeTabKey;
  }, [activeTabKey]);
  useKey("ArrowLeft", arrowLeftHandler, undefined, [activeTabKey, numberOfTabs]);
  useKey("ArrowRight", arrowRightHandler, undefined, [activeTabKey, numberOfTabs]);
  var clickHandler = useCallback(function (value, ref) {
    if (!trackRef.current) {
      console.warn("[useTabs] trackRef is not passed to the DOM node!. Scroll will not work.");
    }

    setActiveTabKey(value);
    scrollTo(ref);
  }, [changeTab]);
  var getControlProps = useCallback(function (value) {
    var ref = tabRefs[value];
    var id = "tab-".concat(uniqueTabIds.current[value]);
    return {
      ref: ref,
      onClick: function onClick() {
        return clickHandler(value, ref);
      },
      'aria-selected': value === activeTabKey,
      tabIndex: value === activeTabKey ? undefined : -1,
      role: "tab",
      id: id,
      active: isActive(value)
    };
  }, [activeTabKey, isActive]);
  var getPanelProps = useCallback(function (value) {
    var id = "tab-panel-".concat(uniqueTabIds.current[value]);
    return {
      id: id,
      role: "tabpanel",
      'aria-labelledby': "tab-".concat(uniqueTabIds.current[value]),
      tabIndex: value === activeTabKey ? 0 : -1,
      key: value
    };
  }, [activeTabKey, isActive]);
  var getTrackProps = useCallback(function () {
    return {
      ref: trackRef,
      role: "tablist"
    };
  }, [trackRef]);
  return {
    activeTabKey: activeTabKey,
    setActiveTabKey: setActiveTabKey,
    getControlProps: getControlProps,
    getPanelProps: getPanelProps,
    isActive: isActive,
    getTrackProps: getTrackProps
  };
};

export default useTabs;